/**
 * Binning of heatmap values into four levels by quartile (the GitHub
 * contribution-graph model): each level holds about a quarter of the distinct
 * values, so a few outliers do not wash every other cell into the lowest shade.
 */

export type NxHeatLevel = 1 | 2 | 3 | 4;

function quantile(sorted: number[], p: number): number {
  const n = sorted.length;
  if (n === 1) return sorted[0]!;
  const h = p * (n - 1);
  const i = Math.floor(h);
  const j = Math.min(i + 1, n - 1);
  return sorted[i]! + (h - i) * (sorted[j]! - sorted[i]!);
}

/**
 * Quartile cutoffs over the sorted distinct values. Linear interpolation at
 * 0.25 / 0.5 / 0.75; the input is never mutated.
 */
export function quartileThresholds(values: readonly number[]): [number, number, number] {
  const distinct = [...new Set(values.filter((v) => Number.isFinite(v)))].sort((a, b) => a - b);
  if (distinct.length === 0) return [0, 0, 0];
  // Fewer than 4 distinct values: anchor explicitly (min → 1, max → 4, middle
  // monotone) instead of interpolating — interpolation loses the anchoring
  // when two distinct values sit within a few ULPs of each other.
  if (distinct.length < 4) {
    const lo = distinct[0]!;
    const mid = distinct.length === 3 ? distinct[1]! : lo;
    return [lo, mid, mid];
  }
  return [quantile(distinct, 0.25), quantile(distinct, 0.5), quantile(distinct, 0.75)];
}

export function heatLevel(value: number, t: readonly [number, number, number]): NxHeatLevel {
  if (!Number.isFinite(value)) return 1;
  if (value <= t[0]) return 1;
  if (value <= t[1]) return 2;
  if (value <= t[2]) return 3;
  return 4;
}

/**
 * Level function for a set of values. Without negatives a 0 is „nothing“: it
 * stays out of the cutoffs and always lands on level 1, so a sparse grid still
 * spreads its real values over all four shades.
 */
export function heatLevels(values: readonly number[]): (value: number) => NxHeatLevel {
  const finite = values.filter((v) => Number.isFinite(v));
  const hasNegatives = finite.some((v) => v < 0);
  const t = quartileThresholds(hasNegatives ? finite : finite.filter((v) => v !== 0));
  return (value) => (!hasNegatives && value === 0 ? 1 : heatLevel(value, t));
}
